import { alpha, Box, Divider, Grid, Modal, Stack, Typography, useColorScheme } from "@mui/material";
import { Constants } from "../../utils/constants";
import { DefaultButton, MainInput, SuccessButton } from "..";
import { useStripe, useElements, CardElement, loadStripe, Elements } from "@stripe/react-stripe-js";
import { useState } from "react";
import { config } from "../../utils/config";


export default function PaymentFullModal(props) {
    const { open, onClose, onBack, onFinish, currentPlan, cardClientSecret, billing_details, errorMessage, loading } = props;

    const { mode } = useColorScheme();
    const currentMode = mode || "system";
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 460,
        maxWidth: 550,
        bgcolor: 'background.paper',
        border: `1px solid ${Constants.Colors[currentMode].border}`,
        borderRadius: "8px",
        boxShadow: 24,
        p: "1.5rem",
    }

    const cardStyle = {
        style: {
            base: {
                color: "#32325d",
                fontFamily: "Arial, sans-serif",
                fontSmoothing: "antialiased",
                fontSize: "16px",
                "::placeholder": {
                    color: "#32325d",
                },
            },
            invalid: {
                fontFamily: "Arial, sans-serif",
                color: "#fa755a",
                iconColor: "#fa755a",
            },
        },
    };

    const stripe = useStripe();
    const elements = useElements();
    const [cardError, setCardError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [billingAddress, setBillingAddress] = useState({
        name: "",
        email: "",
        address: {
            line1: "",
            line2: "",
            city: "",
            state: "",
            country: "",
            postal_code: "",
        }
    });
    const [inputError, setInputError] = useState(false);

    const handleCardChange = async (event) => {
        setCardError(event.error ? event.error.message : "");
        console.log(event?.error?.message);
    };

    const submitCard = () => {
        return new Promise((resolve, reject) => {
            stripe
                .confirmCardSetup(cardClientSecret, {
                    payment_method: {
                        card: elements.getElement(CardElement),
                        billing_details: billing_details,
                    },
                })
                .then(function (result) {
                    if (result.error) {
                        reject(result.error.message);
                    } else {
                        resolve(result);
                    }
                });
        });
    };

    const onSubmit = async () => {
        if (cardError) {
            return;
        }

        if (!billingAddress.name || !billingAddress.email || !billingAddress.address.line1 || !billingAddress.address.city || !billingAddress.address.state || !billingAddress.address.country || !billingAddress.address.postal_code) {
            setInputError(true);
            return;
        }

        setInputError(false);
        try {
            setSubmitting(true);
            const resp = await submitCard();
            const paymentMethod = resp.setupIntent["payment_method"];
            onFinish({
                paymentMethod: paymentMethod,
                billingAddress: billingAddress,
            });

        } catch (e) {
            console.log(e);
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-payment-plans-title"
            aria-describedby="modal-payment-plans-description"
        >
            <Box sx={style}>
                <Typography
                    sx={{
                        fontWeight: Constants.Styles.fontWeight.semiBold,
                        fontSize: Constants.Styles.fontSize.lg,
                        lineHeight: Constants.Styles.lineHeight.lg,
                    }}
                >
                    Payment Details
                </Typography>
                <Box
                    sx={{
                        mt: "1.125rem",
                        backgroundColor: alpha(Constants.Colors[currentMode].secondary, 0.3),
                        padding: "1rem",
                        borderRadius: "0.25rem",
                        border: `1px solid ${Constants.Colors[currentMode].border}`,
                    }}
                >
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: Constants.Styles.fontSize.sm,
                                    lineHeight: Constants.Styles.lineHeight.sm,
                                    color: Constants.Colors[currentMode].mutedForeground,
                                }}
                            >Selected Plan</Typography>
                            <Typography
                                sx={{
                                    fontSize: Constants.Styles.fontSize.lg,
                                    lineHeight: Constants.Styles.lineHeight.lg,
                                    color: Constants.Colors[currentMode].foreground,
                                    fontWeight: Constants.Styles.fontWeight.semiBold,
                                }}
                            >{currentPlan?.plan}</Typography>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: Constants.Styles.fontSize["2xl"],
                                    lineHeight: Constants.Styles.lineHeight["2xl"],
                                    color: Constants.Colors[currentMode].ring,
                                    fontWeight: Constants.Styles.fontWeight.light,
                                }}
                            >${currentPlan?.price}</Typography>
                            <Typography
                                sx={{
                                    fontSize: Constants.Styles.fontSize.sm,
                                    lineHeight: Constants.Styles.lineHeight.sm,
                                    color: Constants.Colors[currentMode].mutedForeground,
                                }}
                            >per month</Typography>
                        </Box>
                    </Stack>
                </Box>
                {errorMessage && <Box
                    marginBlock={"1rem"}
                >
                    <Typography
                        sx={{
                            color: Constants.Colors[currentMode].destructive,
                            fontSize: Constants.Styles.fontSize.sm,
                            lineHeight: Constants.Styles.lineHeight.sm,
                        }}
                    >
                        {errorMessage}
                    </Typography>
                </Box>}
                <Typography
                    sx={{
                        fontSize: Constants.Styles.fontSize.sm,
                        fontWeight: Constants.Styles.fontWeight.medium,
                        lineHeight: Constants.Styles.lineHeight.sm,
                    }}
                    mt={"1.125rem"}
                >Payment Method</Typography>
                <Stack
                    spacing={"1.5rem"}
                    mb={"1.125rem"}
                    mt={"0.5rem"}
                >
                    <CardElement
                        options={cardStyle}
                        onChange={handleCardChange}
                    ></CardElement>
                    {cardError && (
                        <Typography
                            sx={{
                                color: Constants.Colors[currentMode].destructive,
                                fontSize: Constants.Styles.fontSize.sm,
                                lineHeight: Constants.Styles.lineHeight.sm,
                            }}
                        >
                            {cardError}
                        </Typography>
                    )}
                </Stack>
                <Divider />
                <Typography
                    sx={{
                        fontSize: Constants.Styles.fontSize.sm,
                        fontWeight: Constants.Styles.fontWeight.medium,
                        lineHeight: Constants.Styles.lineHeight.sm,
                    }}
                    mt={"1.125rem"}
                >Billing Address</Typography>
                <Stack
                    spacing={"0.5rem"}
                    mb={"1.125rem"}
                    mt={"0.25rem"}
                >
                    <MainInput
                        name="billing_email"
                        placeholder="billing@company.com"
                        value={billingAddress.email}
                        onChange={(e) => {
                            setBillingAddress({
                                ...billingAddress,
                                email: e.target.value,
                            });
                        }}
                        label="Billing Email"
                        type="email"
                        error={inputError && !billingAddress.email}
                        helperText={inputError && !billingAddress.email ? "Please enter your email" : ""}
                    />
                    <MainInput
                        name="billing_name"
                        placeholder="Billing Name"
                        value={billingAddress.name}
                        onChange={(e) => {
                            setBillingAddress({
                                ...billingAddress,
                                name: e.target.value,
                            });
                        }}
                        label="Billing Name"
                        error={inputError && !billingAddress.name}
                        helperText={inputError && !billingAddress.name ? "Please enter your name" : ""}
                    />
                    <MainInput
                        name="address_line1"
                        placeholder="123 Business St"
                        value={billingAddress.address.line1}
                        onChange={(e) => {
                            setBillingAddress({
                                ...billingAddress,
                                address: {
                                    ...billingAddress.address,
                                    line1: e.target.value,
                                }
                            });
                        }}
                        label="Address Line 1"
                        error={inputError && !billingAddress.address.line1}
                        helperText={inputError && !billingAddress.address.line1 ? "Please enter your address line 1" : ""}
                    />
                    <MainInput
                        name="address_line2"
                        placeholder="Suite 100"
                        value={billingAddress.address.line2}
                        onChange={(e) => {
                            setBillingAddress({
                                ...billingAddress,
                                address: {
                                    ...billingAddress.address,
                                    line2: e.target.value,
                                }
                            });
                        }}
                        label="Address Line 2 (optional)"
                    />
                    <Grid
                        container
                        spacing={"1.25rem"}
                        rowSpacing={"0.5rem"}
                    >
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <MainInput
                                name="city"
                                placeholder="City"
                                value={billingAddress.address.city}
                                onChange={(e) => {
                                    setBillingAddress({
                                        ...billingAddress,
                                        address: {
                                            ...billingAddress.address,
                                            city: e.target.value,
                                        }
                                    });
                                }}
                                label="City"
                                error={inputError && !billingAddress.address.city}
                                helperText={inputError && !billingAddress.address.city ? "Please enter your city" : ""}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <MainInput
                                name="state"
                                placeholder="State"
                                value={billingAddress.address.state}
                                onChange={(e) => {
                                    setBillingAddress({
                                        ...billingAddress,
                                        address: {
                                            ...billingAddress.address,
                                            state: e.target.value,
                                        }
                                    });
                                }}
                                label="State / Province"
                                error={inputError && !billingAddress.address.state}
                                helperText={inputError && !billingAddress.address.state ? "Please enter your state" : ""}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <MainInput
                                name="postal_code"
                                placeholder="10001"
                                value={billingAddress.address.postal_code}
                                onChange={(e) => {
                                    setBillingAddress({
                                        ...billingAddress,
                                        address: {
                                            ...billingAddress.address,
                                            postal_code: e.target.value,
                                        }
                                    });
                                }}
                                label="Postal Code"
                                type="number"
                                error={inputError && !billingAddress.address.postal_code}
                                helperText={inputError && !billingAddress.address.postal_code ? "Please enter your postal code" : ""}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6 }}>
                            <MainInput
                                name="country"
                                placeholder="United States"
                                value={billingAddress.address.country}
                                onChange={(e) => {
                                    setBillingAddress({
                                        ...billingAddress,
                                        address: {
                                            ...billingAddress.address,
                                            country: e.target.value,
                                        }
                                    });
                                }}
                                label="Country"
                                error={inputError && !billingAddress.address.country}
                                helperText={inputError && !billingAddress.address.country ? "Please enter your country" : ""}
                            />
                        </Grid>
                    </Grid>
                </Stack>
                <Divider />
                <Stack direction={"row"} alignItems={"center"} justifyContent={"end"} spacing={2} sx={{
                    mt: "2rem",
                }}>
                    <DefaultButton
                        text={"Back"}
                        onClick={onBack}
                        disabled={loading}
                        size="medium"
                        sx={{
                            width: 60,
                        }}
                    />
                    <SuccessButton
                        text={`Subscribe to ${currentPlan?.plan}`}
                        size="medium"
                        sx={{
                            width: 240,
                        }}
                        disabled={loading}
                        onClick={() => {
                            onSubmit();
                        }}
                    />
                </Stack>
            </Box>
        </Modal>
    );
}